import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HistoryModal from '../../components/feature/HistoryModal';
import SeoHead from '../../components/feature/SeoHead';
import { useEffect, useState, useRef } from 'react';

const HomePage = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animation au scroll - CORRIGÉ
  useEffect(() => {
    // Nettoyer l'ancien observer s'il existe
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => {
      if (observerRef.current) {
        observerRef.current.observe(el);
      }
    });

    // Nettoyage complet au démontage
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-dark-bg">
      <SeoHead
        title="Louati AUTOCENTER Sàrl – Esthétique Automobile & Vente Véhicules Payerne"
        description="Louati AUTOCENTER Sàrl à Payerne : esthétique automobile professionnelle, lavage détaillé, polissage, céramique, vente et dépôt-vente de véhicules d'occasion. Service à domicile disponible. Réservation en ligne 24/7."
        keywords="esthétique automobile Payerne, lavage auto Payerne, polissage voiture Suisse, céramique auto, vente véhicules occasion Payerne"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://www.louatiautocenter.ch'}/`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Louati AUTOCENTER Sàrl – Esthétique Automobile & Vente Véhicules Payerne",
          "description": "Expert en esthétique automobile à Payerne. Lavage, polissage, céramique et vente de véhicules. Service à domicile disponible. Réservation en ligne 24/7.",
          "url": `${import.meta.env.VITE_SITE_URL || 'https://www.louatiautocenter.ch'}/`,
          "inLanguage": "fr-CH",
          "isPartOf": {
            "@type": "WebSite",
            "name": "Louati AUTOCENTER Sàrl",
            "url": import.meta.env.VITE_SITE_URL || 'https://www.louatiautocenter.ch'
          },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Accueil", "item": `${import.meta.env.VITE_SITE_URL || 'https://www.louatiautocenter.ch'}/` }
            ]
          }
        }}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/9d4a86ad9a636766fededea21f2a03b2/4b5ad812ea75c57f11bccbfe0b7f296a.png"
            alt="Lamborghini blanche dans garage moderne LOUATI AUTOCENTER"
            className="w-full h-full object-cover object-center"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 max-w-5xl mx-auto animate-fade-in">
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white mb-4 sm:mb-6 leading-tight drop-shadow-2xl">
            L'Excellence Automobile
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-100 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed drop-shadow-lg px-4">
            Detailing premium, vente de véhicules d'exception et expertise automobile à Payerne
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <Link
              to="/reservation"
              className="px-6 sm:px-8 py-3 sm:py-4 bg-primary text-dark-bg rounded-lg font-semibold text-base sm:text-lg hover:bg-primary/90 transition-all hover:scale-105 whitespace-nowrap shadow-xl min-h-[44px] flex items-center justify-center touch-manipulation"
            >
              Réserver une prestation
            </Link>
            <Link
              to="/acheter"
              className="px-6 sm:px-8 py-3 sm:py-4 bg-accent-sky text-white rounded-lg font-semibold text-base sm:text-lg hover:bg-accent-sky/90 transition-all hover:scale-105 whitespace-nowrap shadow-xl min-h-[44px] flex items-center justify-center touch-manipulation"
            >
              Acheter un véhicule
            </Link>
            <Link
              to="/vendre"
              className="px-6 sm:px-8 py-3 sm:py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 rounded-lg font-semibold text-base sm:text-lg hover:bg-white/20 transition-all hover:scale-105 whitespace-nowrap shadow-xl min-h-[44px] flex items-center justify-center touch-manipulation"
            >
              Vendre mon véhicule
            </Link>
            <Link
              to="/cleanpass"
              className="px-6 sm:px-8 py-3 sm:py-4 bg-accent-gold text-dark-bg rounded-lg font-semibold text-base sm:text-lg hover:bg-accent-gold/90 transition-all hover:scale-105 whitespace-nowrap shadow-xl min-h-[44px] flex items-center justify-center gap-2 touch-manipulation"
            >
              <i className="ri-vip-crown-line"></i>
              CleanPass
            </Link>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-4xl text-white/70"></i>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-dark-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 animate-on-scroll opacity-0">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-primary mb-4">
              Nos Services
            </h2>
            <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto px-4">
              Prestations automobiles premium et solutions complètes pour vos véhicules
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {/* Card 1 - Réserver une prestation */}
            <Link
              to="/reservation"
              className="group relative h-80 sm:h-96 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 animate-on-scroll opacity-0 touch-manipulation"
            >
              <img
                src="https://public.readdy.ai/ai/img_res/88b5a03bc7fedb5a95ba4bea451bb753.jpg"
                alt="Réserver une prestation"
                className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white transform group-hover:translate-y-0 transition-transform">
                <div className="inline-block px-3 py-1 bg-primary rounded-full text-xs font-semibold mb-3 text-dark-bg">
                  RÉSERVATION
                </div>
                <h3 className="text-3xl font-bold mb-2">Réserver une Prestation</h3>
                <p className="text-white/90 mb-4">Detailing, nettoyage, polish, céramique</p>
                <div className="flex items-center text-primary font-semibold">
                  <span>Réserver maintenant</span>
                  <i className="ri-arrow-right-line ml-2"></i>
                </div>
              </div>
            </Link>

            {/* Card 2 - Véhicules en stock */}
            <Link
              to="/vehicules"
              className="group relative h-80 sm:h-96 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 animate-on-scroll opacity-0 touch-manipulation"
              style={{animationDelay: '0.1s'}}
            >
              <img
                src="https://readdy.ai/api/search-image?query=Ultra%20realistic%20luxury%20car%20showroom%20photograph%20with%20pristine%20black%20Porsche%20911%20sports%20car%20on%20display%20in%20modern%20minimalist%20dealership%2C%20photorealistic%20professional%20automotive%20photography%20with%20perfect%20reflections%20on%20polished%20floor%2C%20elegant%20spotlighting%20creating%20dramatic%20shadows%2C%20clean%20white%20walls%20and%20contemporary%20architecture%2C%20hyperrealistic%208k%20quality%20showroom%20scene%20with%20natural%20depth%20of%20field&width=500&height=800&seq=service-vente-realistic-v2&orientation=portrait"
                alt="Véhicules en stock"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <div className="inline-block px-3 py-1 bg-accent-sky rounded-full text-xs font-semibold mb-3 text-dark-bg">
                  STOCK
                </div>
                <h3 className="text-3xl font-bold mb-2">Véhicules en Stock</h3>
                <p className="text-white/90 mb-4">Découvrez notre sélection de qualité</p>
                <div className="flex items-center text-accent-sky font-semibold">
                  <span>Voir le stock</span>
                  <i className="ri-arrow-right-line ml-2"></i>
                </div>
              </div>
            </Link>

            {/* Card 3 - Vendre mon véhicule */}
            <Link
              to="/vendre"
              className="group relative h-80 sm:h-96 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 animate-on-scroll opacity-0 touch-manipulation"
              style={{animationDelay: '0.2s'}}
            >
              <img
                src="https://readdy.ai/api/search-image?query=Photorealistic%20professional%20automotive%20expert%20in%20business%20attire%20inspecting%20luxury%20vehicle%20with%20digital%20tablet%20in%20modern%20clean%20garage%2C%20ultra%20realistic%20natural%20lighting%20showing%20detailed%20vehicle%20inspection%20process%2C%20professional%20photographer%20quality%20image%20with%20sharp%20focus%20and%20natural%20colors%2C%20contemporary%20automotive%20service%20environment%20with%20white%20background%2C%20hyperrealistic%208k%20quality%20professional%20photograph&width=500&height=800&seq=service-vendre-realistic-v2&orientation=portrait"
                alt="Vendre mon véhicule"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <div className="inline-block px-3 py-1 bg-accent-gold rounded-full text-xs font-semibold mb-3 text-dark-bg">
                  DÉPÔT-VENTE
                </div>
                <h3 className="text-3xl font-bold mb-2">Confiez-nous votre Véhicule</h3>
                <p className="text-white/90 mb-4">Nous vendons votre véhicule pour vous</p>
                <div className="flex items-center text-accent-gold font-semibold">
                  <span>En savoir plus</span>
                  <i className="ri-arrow-right-line ml-2"></i>
                </div>
              </div>
            </Link>

            {/* Card 4 - Acheter un véhicule */}
            <Link
              to="/acheter"
              className="group relative h-80 sm:h-96 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 animate-on-scroll opacity-0 touch-manipulation"
              style={{animationDelay: '0.3s'}}
            >
              <img
                src="https://readdy.ai/api/search-image?query=Ultra%20realistic%20photograph%20of%20happy%20customer%20receiving%20car%20keys%20from%20professional%20automotive%20dealer%20in%20modern%20luxury%20showroom%2C%20photorealistic%20scene%20with%20genuine%20smiles%20and%20natural%20body%20language%2C%20beautiful%20new%20luxury%20vehicle%20visible%20in%20soft%20focus%20background%2C%20professional%20photography%20with%20natural%20lighting%20and%20authentic%20emotions%2C%20hyperrealistic%208k%20quality%20lifestyle%20automotive%20photograph%20with%20cinematic%20composition&width=500&height=800&seq=service-acheter-realistic-v2&orientation=portrait"
                alt="Acheter un véhicule"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <div className="inline-block px-3 py-1 bg-accent-peach rounded-full text-xs font-semibold mb-3 text-dark-bg">
                  ACHAT
                </div>
                <h3 className="text-3xl font-bold mb-2">Acheter un Véhicule</h3>
                <p className="text-white/90 mb-4">Financement et garantie disponibles</p>
                <div className="flex items-center text-accent-peach font-semibold">
                  <span>Découvrir nos offres</span>
                  <i className="ri-arrow-right-line ml-2"></i>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Prestations Detailing Section */}
      <section className="py-24 bg-dark-bg">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16 animate-on-scroll opacity-0">
            <h2 className="font-serif text-5xl lg:text-6xl font-bold text-primary mb-4">
              Nos Prestations Detailing 🚗
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Des services automobiles premium adaptés à tous vos besoins
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Card 1 - Nettoyage */}
            <Link
              to="/prestations"
              className="group relative h-96 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 animate-on-scroll opacity-0 touch-manipulation"
            >
              <img
                src="https://readdy.ai/api/search-image?query=Photorealistic%20close-up%20of%20pristine%20luxury%20car%20interior%20with%20immaculate%20black%20leather%20seats%20and%20polished%20dashboard%2C%20ultra%20realistic%20professional%20automotive%20photography%20showing%20perfect%20cleanliness%20and%20rich%20textures%2C%20natural%20soft%20lighting%20highlighting%20leather%20grain%20details%2C%20hyperrealistic%208k%20quality%20interior%20photograph%20with%20sharp%20focus%20and%20natural%20depth%2C%20professional%20detailing%20results%20showcase&width=600&height=800&seq=service-nettoyage-realistic-v2&orientation=portrait"
                alt="Nettoyage"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <div className="inline-block px-3 py-1 bg-primary rounded-full text-xs font-semibold mb-3 text-dark-bg">
                  EXPRESS
                </div>
                <h3 className="text-3xl font-bold mb-2">Nettoyage</h3>
                <p className="text-white/90 mb-4">Express, Approfondi ou Suprême</p>
                <p className="text-white/70 text-sm">dès 69 CHF</p>
              </div>
            </Link>

            {/* Card 2 - Polish */}
            <Link
              to="/prestations"
              className="group relative h-96 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 animate-on-scroll opacity-0 touch-manipulation"
              style={{animationDelay: '0.1s'}}
            >
              <img
                src="https://readdy.ai/api/search-image?query=Ultra%20realistic%20macro%20photograph%20of%20glossy%20black%20car%20paint%20surface%20with%20perfect%20mirror%20reflection%20showing%20professional%20paint%20correction%20results%2C%20photorealistic%20water%20droplets%20on%20flawless%20automotive%20finish%20with%20incredible%20detail%2C%20natural%20lighting%20creating%20beautiful%20reflections%2C%20hyperrealistic%208k%20quality%20automotive%20photography%20showing%20pristine%20paint%20depth%20and%20clarity%20against%20clean%20studio%20background&width=600&height=800&seq=service-polish-realistic-v2&orientation=portrait"
                alt="Polish & Correction"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <div className="inline-block px-3 py-1 bg-accent-sky rounded-full text-xs font-semibold mb-3 text-dark-bg">
                  CORRECTION
                </div>
                <h3 className="text-3xl font-bold mb-2">Polish & Correction</h3>
                <p className="text-white/90 mb-4">Lustrage et correction peinture</p>
                <p className="text-white/70 text-sm">dès 299 CHF</p>
              </div>
            </Link>

            {/* Card 3 - Céramique */}
            <Link
              to="/prestations"
              className="group relative h-96 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 animate-on-scroll opacity-0 touch-manipulation"
              style={{animationDelay: '0.2s'}}
            >
              <img
                src="https://readdy.ai/api/search-image?query=Photorealistic%20macro%20shot%20of%20crystal%20clear%20water%20beads%20perfectly%20rolling%20off%20ceramic%20coated%20luxury%20car%20surface%2C%20ultra%20realistic%20hydrophobic%20protection%20demonstration%20with%20sharp%20detailed%20droplets%20on%20glossy%20black%20paint%2C%20natural%20lighting%20creating%20beautiful%20refractions%20through%20water%2C%20hyperrealistic%208k%20quality%20automotive%20photography%20showing%20ceramic%20coating%20effectiveness%20against%20minimalist%20white%20background&width=600&height=800&seq=service-ceramique-realistic-v2&orientation=portrait"
                alt="Céramique"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <div className="inline-block px-3 py-1 bg-accent-gold rounded-full text-xs font-semibold mb-3 text-dark-bg">
                  PROTECTION
                </div>
                <h3 className="text-3xl font-bold mb-2">Céramique</h3>
                <p className="text-white/90 mb-4">Protection 2 à 8 ans</p>
                <p className="text-white/70 text-sm">dès 800 CHF</p>
              </div>
            </Link>

            {/* Card 4 - Services Additionnels */}
            <Link
              to="/services-additionnels"
              className="group relative h-96 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 animate-on-scroll opacity-0 touch-manipulation"
              style={{animationDelay: '0.3s'}}
            >
              <img
                src="https://readdy.ai/api/search-image?query=Photorealistic%20professional%20automotive%20services%20scene%20showing%20tinted%20car%20windows%20installation%20alloy%20wheels%20display%20and%20vehicle%20maintenance%20in%20modern%20clean%20workshop%2C%20ultra%20realistic%20professional%20photography%20with%20quality%20equipment%20and%20tools%20visible%2C%20natural%20lighting%20in%20contemporary%20garage%20environment%2C%20hyperrealistic%208k%20quality%20automotive%20service%20photograph%20with%20white%20background&width=600&height=800&seq=service-additionnels-home-v1&orientation=portrait"
                alt="Services Additionnels"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <div className="inline-block px-3 py-1 bg-accent-peach rounded-full text-xs font-semibold mb-3 text-dark-bg">
                  SERVICES
                </div>
                <h3 className="text-3xl font-bold mb-2">Services Additionnels</h3>
                <p className="text-white/90 mb-4">Vitres teintées, jantes, entretien</p>
                <p className="text-white/70 text-sm">Sur devis</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Service à Domicile Section */}
      <section className="py-24 bg-dark-card">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="font-serif text-5xl lg:text-6xl font-bold text-primary mb-16 animate-on-scroll opacity-0">
            Service à<br />Domicile
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-dark-bg border border-dark-border rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-500 hover:-translate-y-2 animate-on-scroll opacity-0 touch-manipulation">
              <div className="h-64 bg-dark-bg flex items-center justify-center p-8">
                <img
                  src="https://readdy.ai/api/search-image?query=Photorealistic%20professional%20mobile%20car%20detailing%20van%20arriving%20at%20modern%20residential%20home%20driveway%2C%20ultra%20realistic%20scene%20with%20branded%20service%20vehicle%20and%20equipment%20visible%2C%20beautiful%20contemporary%20house%20in%20background%20with%20natural%20daylight%2C%20hyperrealistic%208k%20quality%20lifestyle%20photograph%20showing%20convenient%20at-home%20automotive%20service%20with%20natural%20colors%20and%20authentic%20setting&width=500&height=400&seq=domicile-realistic-v1&orientation=landscape"
                  alt="À votre domicile"
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <h3 className="font-serif text-2xl font-bold text-primary mb-2">
                  À votre domicile
                </h3>
                <p className="text-gray-400">
                  Nous venons directement chez vous pour un service pratique et sans contrainte
                </p>
              </div>
            </div>

            <div className="bg-dark-bg border border-dark-border rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-500 hover:-translate-y-2 animate-on-scroll opacity-0 touch-manipulation" style={{animationDelay: '0.1s'}}>
              <div className="h-64 bg-dark-bg flex items-center justify-center p-8">
                <img
                  src="https://readdy.ai/api/search-image?query=Ultra%20realistic%20photograph%20of%20car%20keys%20being%20handed%20over%20between%20customer%20and%20professional%20detailer%2C%20photorealistic%20close-up%20shot%20with%20natural%20hand%20gestures%20and%20genuine%20interaction%2C%20modern%20clean%20automotive%20service%20environment%20in%20soft%20focus%20background%2C%20hyperrealistic%208k%20quality%20lifestyle%20photography%20showing%20convenient%20vehicle%20pickup%20and%20delivery%20service%20with%20natural%20lighting&width=500&height=400&seq=domicile-realistic-v2&orientation=landscape"
                  alt="Prise en charge"
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <h3 className="font-serif text-2xl font-bold text-primary mb-2">
                  Prise en charge
                </h3>
                <p className="text-gray-400">
                  Nous récupérons votre véhicule et vous le ramenons impeccable
                </p>
              </div>
            </div>

            <div className="bg-dark-bg border border-dark-border rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-500 hover:-translate-y-2 animate-on-scroll opacity-0 touch-manipulation" style={{animationDelay: '0.2s'}}>
              <div className="h-64 bg-dark-bg flex items-center justify-center p-8">
                <img
                  src="https://readdy.ai/api/search-image?query=Photorealistic%20modern%20automotive%20workshop%20garage%20interior%20with%20professional%20detailing%20equipment%20and%20pristine%20clean%20environment%2C%20ultra%20realistic%20photograph%20showing%20high-end%20service%20facility%20with%20polished%20floors%20and%20organized%20tools%2C%20natural%20lighting%20through%20large%20windows%2C%20hyperrealistic%208k%20quality%20professional%20automotive%20workshop%20photography%20with%20white%20walls%20and%20contemporary%20design&width=500&height=400&seq=domicile-realistic-v3&orientation=landscape"
                  alt="Au garage"
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <h3 className="font-serif text-2xl font-bold text-primary mb-2">
                  Au garage
                </h3>
                <p className="text-gray-400">
                  Déposez votre véhicule dans notre atelier équipé et professionnel
                </p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <Link
              to="/devis"
              className="inline-flex items-center px-8 py-4 bg-primary text-dark-bg rounded-lg font-semibold text-lg hover:bg-primary/90 transition-all hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-file-list-3-line text-xl mr-3"></i>
              Demander un devis gratuit
            </Link>
          </div>
        </div>
      </section>

      {/* Avis Clients Section */}
      <section className="py-24 bg-dark-bg">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16 animate-on-scroll opacity-0">
            <h2 className="font-serif text-5xl lg:text-6xl font-bold text-primary mb-4">
              Avis Clients
            </h2>
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-3xl text-accent-gold"></i>
                ))}
              </div>
              <span className="text-2xl font-bold text-white ml-2">5.0</span>
            </div>
            <p className="text-gray-400 text-lg">
              Ce que nos clients disent de nous
            </p>
            <a
              href="https://share.google/Ebd9m9hduxJDVdRrb"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-4 text-primary hover:text-primary/80 transition-colors"
            >
              <i className="ri-google-fill text-xl"></i>
              <span className="font-semibold">Voir tous nos avis Google</span>
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Avis 1 */}
            <div className="bg-dark-card border border-dark-border rounded-2xl p-8 hover:shadow-xl transition-all duration-500 hover:-translate-y-2 animate-on-scroll opacity-0 touch-manipulation">
              <div className="flex items-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-xl text-accent-gold"></i>
                ))}
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                "Service impeccable ! Mon véhicule est ressorti comme neuf. L'équipe est professionnelle et très à l'écoute. Je recommande vivement !"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <i className="ri-user-line text-2xl text-primary"></i>
                </div>
                <div>
                  <p className="font-semibold text-white">Marc D.</p>
                  <p className="text-sm text-gray-500">Client vérifié</p>
                </div>
              </div>
            </div>

            {/* Avis 2 */}
            <div className="bg-dark-card border border-dark-border rounded-2xl p-8 hover:shadow-xl transition-all duration-500 hover:-translate-y-2 animate-on-scroll opacity-0 touch-manipulation" style={{animationDelay: '0.1s'}}>
              <div className="flex items-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-xl text-accent-gold"></i>
                ))}
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                "Excellent travail sur la céramique de ma voiture. Le résultat est bluffant et la protection est vraiment efficace. Très satisfait !"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-accent-sky/20 flex items-center justify-center">
                  <i className="ri-user-line text-2xl text-accent-sky"></i>
                </div>
                <div>
                  <p className="font-semibold text-white">Sophie L.</p>
                  <p className="text-sm text-gray-500">Client vérifié</p>
                </div>
              </div>
            </div>

            {/* Avis 3 */}
            <div className="bg-dark-card border border-dark-border rounded-2xl p-8 hover:shadow-xl transition-all duration-500 hover:-translate-y-2 animate-on-scroll opacity-0 touch-manipulation" style={{animationDelay: '0.2s'}}>
              <div className="flex items-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-xl text-accent-gold"></i>
                ))}
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                "Service à domicile très pratique. Ils sont venus chez moi, ont fait un travail remarquable. Je n'ai plus à me déplacer !"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-accent-gold/20 flex items-center justify-center">
                  <i className="ri-user-line text-2xl text-accent-gold"></i>
                </div>
                <div>
                  <p className="font-semibold text-white">Thomas R.</p>
                  <p className="text-sm text-gray-500">Client vérifié</p>
                </div>
              </div>
            </div>

            {/* Avis 4 */}
            <div className="bg-dark-card border border-dark-border rounded-2xl p-8 hover:shadow-xl transition-all duration-500 hover:-translate-y-2 animate-on-scroll opacity-0 touch-manipulation" style={{animationDelay: '0.3s'}}>
              <div className="flex items-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-xl text-accent-gold"></i>
                ))}
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                "Professionnel et passionné ! Le polish de ma voiture est parfait, plus aucune rayure visible. Un grand merci à toute l'équipe."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-accent-peach/20 flex items-center justify-center">
                  <i className="ri-user-line text-2xl text-accent-peach"></i>
                </div>
                <div>
                  <p className="font-semibold text-white">Julie M.</p>
                  <p className="text-sm text-gray-500">Client vérifié</p>
                </div>
              </div>
            </div>

            {/* Avis 5 */}
            <div className="bg-dark-card border border-dark-border rounded-2xl p-8 hover:shadow-xl transition-all duration-500 hover:-translate-y-2 animate-on-scroll opacity-0 touch-manipulation" style={{animationDelay: '0.4s'}}>
              <div className="flex items-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-xl text-accent-gold"></i>
                ))}
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                "Achat d'un véhicule en toute confiance. Transparence totale, véhicule en excellent état. Je recommande sans hésitation !"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <i className="ri-user-line text-2xl text-primary"></i>
                </div>
                <div>
                  <p className="font-semibold text-white">Alexandre P.</p>
                  <p className="text-sm text-gray-500">Client vérifié</p>
                </div>
              </div>
            </div>

            {/* Avis 6 */}
            <div className="bg-dark-card border border-dark-border rounded-2xl p-8 hover:shadow-xl transition-all duration-500 hover:-translate-y-2 animate-on-scroll opacity-0 touch-manipulation" style={{animationDelay: '0.5s'}}>
              <div className="flex items-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-xl text-accent-gold"></i>
                ))}
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                "Nettoyage intérieur/extérieur au top ! Ma voiture sent bon et brille de mille feux. Rapport qualité-prix excellent."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-accent-sky/20 flex items-center justify-center">
                  <i className="ri-user-line text-2xl text-accent-sky"></i>
                </div>
                <div>
                  <p className="font-semibold text-white">Céline B.</p>
                  <p className="text-sm text-gray-500">Client vérifié</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12 animate-on-scroll opacity-0">
            <a
              href="https://share.google/Ebd9m9hduxJDVdRrb"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-dark-bg rounded-lg font-semibold text-lg hover:bg-primary/90 transition-all hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-google-fill text-xl"></i>
              Laisser un avis sur Google
              <i className="ri-external-link-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Ultra%20realistic%20professional%20automotive%20detailing%20workshop%20scene%20with%20luxury%20car%20being%20serviced%20by%20expert%20technician%20in%20modern%20clean%20facility%2C%20photorealistic%20professional%20photography%20with%20natural%20lighting%20and%20professional%20equipment%20visible%2C%20hyperrealistic%208k%20quality%20automotive%20service%20photograph%20creating%20premium%20atmosphere%20with%20authentic%20details%20and%20cinematic%20composition&width=1920&height=600&seq=cta-realistic-v1&orientation=landscape"
            alt="Réservez maintenant"
            className="w-full h-full object-cover object-center"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className="flex flex-col lg:flex-row items-center justify-between">
            <h2 className="text-white text-5xl lg:text-7xl font-bold mb-8 lg:mb-0 animate-on-scroll opacity-0">
              réservez<br />maintenant
            </h2>

            <div className="text-white text-right mb-8 lg:mb-0 animate-on-scroll opacity-0" style={{animationDelay: '0.1s'}}>
              <p className="text-lg lg:text-xl font-light leading-relaxed">
                Prenez rendez-vous en ligne<br />
                et payez en toute sécurité
              </p>
            </div>

            <Link
              to="/reservation"
              className="group flex items-center justify-between bg-primary text-dark-bg px-8 py-4 rounded-full hover:bg-primary/90 transition-all duration-300 whitespace-nowrap transform hover:scale-105 hover:shadow-2xl animate-on-scroll opacity-0"
              style={{animationDelay: '0.2s'}}
            >
              <span className="font-semibold mr-4">Prendre rendez-vous</span>
              <div className="w-10 h-10 flex items-center justify-center bg-dark-bg text-primary rounded-full group-hover:scale-110 transition-transform">
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="bg-dark-card text-white py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16 animate-on-scroll opacity-0">
            <p className="text-gray-500 text-sm uppercase tracking-wider mb-4">
              À propos
            </p>
            <h2 className="font-serif text-5xl lg:text-7xl font-bold leading-tight text-primary">
              Excellence<br />Professionnalisme<br />Passion
            </h2>
          </div>

          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-16 animate-on-scroll opacity-0">
            <button 
              onClick={() => setIsHistoryModalOpen(true)}
              className="px-8 py-3 bg-primary text-dark-bg rounded-lg font-semibold hover:bg-primary/90 transition-all duration-300 whitespace-nowrap transform hover:scale-105 cursor-pointer touch-manipulation"
            >
              Notre Histoire
            </button>
            <a
              href="tel:+41796840588"
              className="px-8 py-3 border-2 border-primary text-primary rounded-lg font-semibold hover:bg-primary hover:text-dark-bg transition-all duration-300 whitespace-nowrap transform hover:scale-105"
            >
              Nous Contacter
            </a>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="px-6 py-3 border border-dark-border rounded-lg font-semibold hover:bg-dark-card transition-colors whitespace-nowrap text-gray-300 cursor-pointer touch-manipulation"
            >
              Retour
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            <div className="bg-dark-bg rounded-2xl p-6 flex items-center space-x-4 hover:bg-dark-border transition-all duration-300 transform hover:scale-105 animate-on-scroll opacity-0 touch-manipulation">
              <i className="ri-shield-check-line text-3xl text-primary"></i>
              <span className="font-medium text-gray-300">Qualité garantie</span>
            </div>
            <div className="bg-dark-bg rounded-2xl p-6 flex items-center space-x-4 hover:bg-dark-border transition-all duration-300 transform hover:scale-105 animate-on-scroll opacity-0 touch-manipulation" style={{animationDelay: '0.1s'}}>
              <i className="ri-time-line text-3xl text-accent-sky"></i>
              <span className="font-medium text-gray-300">Service rapide</span>
            </div>
            <div className="bg-dark-bg rounded-2xl p-6 flex items-center space-x-4 hover:bg-dark-border transition-all duration-300 transform hover:scale-105 animate-on-scroll opacity-0 touch-manipulation" style={{animationDelay: '0.2s'}}>
              <i className="ri-star-line text-3xl text-accent-gold"></i>
              <span className="font-medium text-gray-300">Expertise reconnue</span>
            </div>
            <div className="bg-dark-bg rounded-2xl p-6 flex items-center space-x-4 hover:bg-dark-border transition-all duration-300 transform hover:scale-105 animate-on-scroll opacity-0 touch-manipulation" style={{animationDelay: '0.3s'}}>
              <i className="ri-home-smile-line text-3xl text-accent-peach"></i>
              <span className="font-medium text-gray-300">Service à domicile</span>
            </div>
            <div className="bg-dark-bg rounded-2xl p-6 flex items-center space-x-4 hover:bg-dark-border transition-all duration-300 transform hover:scale-105 animate-on-scroll opacity-0 touch-manipulation" style={{animationDelay: '0.4s'}}>
              <i className="ri-secure-payment-line text-3xl text-primary"></i>
              <span className="font-medium text-gray-300">Paiement sécurisé</span>
            </div>
          </div>
        </div>
      </section>

      {/* Localisation Section */}
      <section className="py-24 bg-dark-bg">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16 animate-on-scroll opacity-0">
            <h2 className="font-serif text-5xl lg:text-6xl font-bold text-primary mb-4">
              Où nous trouver
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Notre garage est situé à Payerne, facilement accessible
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Carte Google Maps */}
            <div className="animate-on-scroll opacity-0">
              <div className="bg-dark-card border border-dark-border rounded-2xl overflow-hidden shadow-xl h-[500px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2732.8!2d6.9375!3d46.8225!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478dc1e8b8b8b8b8%3A0x1234567890abcdef!2sZ.I.%20Champ%20Cheval%202%2C%201530%20Payerne%2C%20Suisse!5e0!3m2!1sfr!2sch!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Localisation LOUATI AUTOCENTER Payerne"
                ></iframe>
              </div>
            </div>

            {/* Informations de contact */}
            <div className="space-y-8 animate-on-scroll opacity-0" style={{animationDelay: '0.1s'}}>
              {/* Adresse */}
              <div className="bg-dark-card border border-dark-border rounded-2xl p-8 hover:shadow-xl transition-all duration-300 touch-manipulation">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-primary"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Adresse</h3>
                    <p className="text-gray-400 leading-relaxed">
                      Z.I. Champ Cheval 2<br />
                      1530 Payerne<br />
                      Suisse
                    </p>
                    <a
                      href="https://www.google.com/maps/dir/?api=1&destination=Z.I.+Champ+Cheval+2,+1530+Payerne,+Suisse"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-4 text-primary hover:text-primary/80 transition-colors font-semibold"
                    >
                      <i className="ri-navigation-line"></i>
                      Obtenir l'itinéraire
                    </a>
                  </div>
                </div>
              </div>

              {/* Téléphone */}
              <div className="bg-dark-card border border-dark-border rounded-2xl p-8 hover:shadow-xl transition-all duration-300 touch-manipulation">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-accent-sky/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-2xl text-accent-sky"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Téléphone</h3>
                    <a
                      href="tel:+41796840588"
                      className="text-gray-400 hover:text-accent-sky transition-colors text-lg"
                    >
                      +41 79 684 05 88
                    </a>
                    <p className="text-gray-500 text-sm mt-2">
                      Lundi - Samedi : 8h00 - 18h00
                    </p>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="bg-dark-card border border-dark-border rounded-2xl p-8 hover:shadow-xl transition-all duration-300 touch-manipulation">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-accent-gold/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-2xl text-accent-gold"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Email</h3>
                    <a
                      href="mailto:contact@louatiautocenter.ch"
                      className="text-gray-400 hover:text-accent-gold transition-colors break-all"
                    >
                      contact@louatiautocenter.ch
                    </a>
                    <p className="text-gray-500 text-sm mt-2">
                      Réponse sous 24h
                    </p>
                  </div>
                </div>
              </div>

              {/* Horaires */}
              <div className="bg-dark-card border border-dark-border rounded-2xl p-8 hover:shadow-xl transition-all duration-300 touch-manipulation" style={{animationDelay: '0.2s'}}>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-accent-peach/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-2xl text-accent-peach"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-4">Horaires d'ouverture</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Lundi - Vendredi</span>
                        <span className="text-white font-semibold">8h00 - 18h00</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Samedi</span>
                        <span className="text-white font-semibold">8h00 - 16h00</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Dimanche</span>
                        <span className="text-gray-500">Fermé</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bouton CTA */}
              <div className="text-center pt-4">
                <Link
                  to="/reservation"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-primary text-dark-bg rounded-lg font-semibold text-lg hover:bg-primary/90 transition-all hover:scale-105 whitespace-nowrap shadow-xl"
                >
                  <i className="ri-calendar-check-line text-xl"></i>
                  Prendre rendez-vous
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partenaires Section */}
      <section className="py-14 bg-dark-card border-t border-dark-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10 animate-on-scroll opacity-0">
            <p className="text-xs uppercase tracking-[0.3em] text-gray-500 font-semibold mb-2">Nos Partenaires</p>
            <div className="w-12 h-px bg-primary mx-auto"></div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-10 sm:gap-16 animate-on-scroll opacity-0">
            {/* AutoScout24 */}
            <a
              href="https://www.autoscout24.ch"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-center w-40 h-16 grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all duration-300 cursor-pointer"
              title="AutoScout24"
            >
              <img
                src="https://storage.readdy-site.link/project_files/5c07145b-cdc7-4101-bfb0-bfdafffdeae5/2fbba636-3297-482f-bc33-010e29d61aa0_AS24_LOGO_gelb.jpg?v=2f7cd157f3c0576c57ab737203783a1c"
                alt="AutoScout24 partenaire LOUATI AUTOCENTER"
                className="max-h-12 max-w-full object-contain"
              />
            </a>
          </div>
        </div>
      </section>

      <Footer />

      {/* History Modal */}
      <HistoryModal 
        isOpen={isHistoryModalOpen} 
        onClose={() => setIsHistoryModalOpen(false)} 
      />
    </div>
  );
};

export default HomePage;
